// Formatage de l'OS + version pour l'affichage admin (page /plugins).
//
// `osName`    vient du heartbeat plugin (UA-derive) : "Windows" | "MacOS" |
//             "Linux" | "Android" | "iOS" | "Unknown" (plugin/.../heartbeat.js).
// `osVersion` vient des User-Agent Client Hints (plugin v1.7+, Chromium only) :
//             `platformVersion` BRUT (ex "10.0.0", "13.0.0", "14.6.1"). null
//             sur Firefox/Safari et plugins < v1.7.
//
// On formate ICI (et pas dans le plugin) pour ajuster les regles sans
// redeploiement Web Store — meme philosophie que userAgent.ts:parseBrowser.

/** Jolie casse du nom d'OS (le plugin envoie "MacOS"). */
export function prettyOsName(osName: string | null | undefined): string {
  if (!osName) return "";
  const n = osName.trim();
  if (/^macos$/i.test(n)) return "macOS";
  if (/^ios$/i.test(n)) return "iOS";
  return n; // Windows / Linux / Android / Unknown ...
}

/**
 * Libelle OS + version. Ex : "Windows 11", "macOS 14.6", "Android 13", "Linux".
 * Sans version exploitable -> nom seul. Rien de connu -> "—".
 *
 * Windows : la `platformVersion` Client Hints encode la release Windows (mapping
 *   Microsoft) — major >= 13 => 11, 1..12 => 10, 0.x => 7/8. C'est la SEULE
 *   facon de distinguer Windows 10 de 11 (le user-agent dit "NT 10.0" pour les 2).
 * macOS : version reelle (ex "14.6.1") -> garde major.minor.
 * Android : `platformVersion` = version Android (ex "13.0.0" -> "Android 13").
 * Linux : Client Hints ne fournit pas de version -> nom seul.
 */
export function formatOs(
  osName: string | null | undefined,
  osVersion: string | null | undefined,
): string {
  const name = prettyOsName(osName);
  const version = (osVersion ?? "").trim();

  if (!name) return version || "—";
  if (!version) return name;

  const major = parseInt(version.split(".")[0] ?? "", 10);

  if (/^windows$/i.test(name)) {
    if (Number.isNaN(major)) return name;
    if (major >= 13) return "Windows 11";
    if (major >= 1) return "Windows 10";
    return "Windows 7/8"; // 0.x
  }

  if (name === "macOS") {
    // major.minor (ex "14.6.1" -> "14.6"), en retirant un ".0" final superflu.
    const short = version.split(".").slice(0, 2).join(".").replace(/\.0$/, "");
    return `macOS ${short}`;
  }

  // Android / iOS / autres : afficher la version majeure.
  if (Number.isNaN(major)) return name;
  return `${name} ${major}`;
}
